import DatePicker from '@/components/features/shared/DatePicker'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Stepper, type Step } from '@/components/ui/stepper'
import {
  CreateAgreementData,
  createBaseAgreementSchema,
} from '@/domain/agreements/agreement'
import { CollectorStatus, getCollectorFullName } from '@/domain/collectors/collector'
import { ServiceType } from '@/domain/offerings/offering'
import { formatSellCode, SellCode } from '@/domain/parcels/parcel'
import { useCreateAgreement } from '@/hooks/api/useAgreements'
import { useClient } from '@/hooks/api/useClients'
import { useCollectors } from '@/hooks/api/useCollectors'
import { useOfferings } from '@/hooks/api/useOfferings'
import { useParcel } from '@/hooks/api/useParcels'
import { useLatestContractNumber } from '@/hooks/api/useAgreements'
import { Paths } from '@/routes/paths'
import { convertToDigitStr } from '@/utils/helpers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { CreditCard, DollarSign, Loader2, MapPin, Wallet } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { AgreementSummary } from '../../agreements/creation/AgreementSummary'
import { ParcelClientSelector } from './ParcelClientSelector'
import { useSellers } from '@/hooks/api/useSellers'
import { installmentsOptions } from '@/constants/installmentOptions'
import { PageContainer } from '../../shared/PageContainer'
import { NavHeader } from '../../shared/NavHeader'

interface SellNewParcelCmpProps {
  parcelId: string
}

type SellStep = 'selection' | 'summary'

export const SellNewParcelCmp = ({ parcelId }: SellNewParcelCmpProps) => {
  const navigate = useNavigate()
  const { data: parcel, isLoading: isLoadingParcel } = useParcel(parcelId)
  const { data: offerings } = useOfferings()
  const createMutation = useCreateAgreement()

  const { data: collectors = [] } = useCollectors({ status: CollectorStatus.Active })
  const { data: sellers = [] } = useSellers({ status: CollectorStatus.Active })

  const [step, setStep] = useState<SellStep>('selection')
  const [paymentMethod, setPaymentMethod] = useState<'full' | 'plan'>('full')
  const [showInitialAmount, setShowInitialAmount] = useState(false)

  const parcelOffering = offerings?.find(o => o.code === '2')
  const { data: latestContractNumber } = useLatestContractNumber(ServiceType.PARCELAS)
  const parcelSchema = createBaseAgreementSchema(ServiceType.PARCELAS)

  const form = useForm<CreateAgreementData>({
    resolver: zodResolver(parcelSchema as any),
    defaultValues: {
      userId: '',
      offeringId: '',
      serviceType: ServiceType.PARCELAS,
      notes: '',
      startDate: new Date(),
      amount: 0,
      hasPaymentPlan: false,
      installmentAmount: undefined,
      installments: undefined,
      parcelId: parcelId,
      sellCode: SellCode.PRENECESSITY,
      contractNumber: undefined,
    },
  })

  useEffect(() => {
    if (latestContractNumber) {
      const match = latestContractNumber.match(/^(P-)(\d+)$/);
      if (match) {
        const nextNumber = parseInt(match[2], 10) + 1;
        form.setValue('contractNumber', `${match[1]}${String(nextNumber).padStart(match[2].length, '0')}`);
      } else {
        form.setValue('contractNumber', latestContractNumber!);
      }
    }
  }, [latestContractNumber])

  const amount = form.watch('amount')
  const initialAmount = form.watch('initialAmount')
  const installmentAmount = form.watch('installmentAmount')
  const installments = form.watch('installments')
  const installmentsValue = installmentAmount && installments ? installmentAmount / installments : 0
  const seller = sellers.find(s => s.id === form.watch('sellerId'))
  const collector = collectors.find(c => c.id === form.watch('collectorId'))

  useEffect(() => {
    if (paymentMethod === 'full') {
      form.setValue('amount', amount)
      form.setValue('hasPaymentPlan', false)
      form.setValue('installments', undefined)
      form.setValue('installmentAmount', undefined)
    } else if (paymentMethod === 'plan') {
      form.setValue('hasPaymentPlan', true)
      form.setValue('installments', 3)
    }
  }, [paymentMethod, amount])

  useEffect(() => {
    if (paymentMethod === 'plan') {
      const totalAmount = (initialAmount || 0) + (installmentAmount || 0)
      form.setValue('amount', totalAmount, { shouldValidate: true })
    }
  }, [paymentMethod, initialAmount, installmentAmount])

  useEffect(() => {
    if (parcelOffering) {
      form.setValue('offeringId', parcelOffering.id)
    }
  }, [parcelOffering])


  const userId = form.watch('userId')
  const { data: client } = useClient(userId)

  if (isLoadingParcel) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!parcel) {
    return <div>Parcela no encontrada</div>
  }

  const steps: Step[] = [
    { id: 'selection', label: 'Contratación', description: 'Cliente y Monto' },
    { id: 'summary', label: 'Resumen', description: 'Confirmar Venta' }
  ]

  const onNext = async () => {
    const isValid = await form.trigger()
    if (isValid) {
      setStep('summary')
    }
  }

  const onSubmit = async (data: CreateAgreementData) => {
    try {
      const payload = { ...data, parcelId: parcel?.id }
      const response = await createMutation.mutateAsync(payload)
      navigate({ to: `${Paths.Management.Contracts}/${response.agreement.id}/${Paths.Management.Payments}/${response.payment.id}` })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <PageContainer className='max-w-5xl'>
      <NavHeader>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Venta de Parcela</h1>
          <p className="text-muted-foreground">
            Creando un nuevo contrato para la parcela <span className="font-bold text-foreground">#{parcel.parcela}</span> (Sección {parcel.section}, Fila {parcel.fila})
          </p>
        </div>
      </NavHeader>

      <div className="mb-8">
        <Stepper steps={steps} currentStep={step} />
      </div>

      {step === 'selection' && (
        <div className="space-y-6">
          {/* Parcel info - separate card */}
          <Card className="border-blue-100 bg-blue-50/30">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2 text-blue-700">
                <MapPin className="h-5 w-5" />
                <CardTitle className="text-lg">Información de la Parcela</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Sección</p>
                  <p className="font-medium text-lg">{parcel.section}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fila</p>
                  <p className="font-medium text-lg">{parcel.fila}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Parcela</p>
                  <p className="font-medium text-lg">#{parcel.parcela}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Single card with all form fields in two columns */}
          <Card>
            <CardHeader>
              <CardTitle>Formulario de Venta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Two-column grid for form fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Column 1: Client Selection */}
                <div className="space-y-2">
                  {/* <label className="text-sm font-medium">Seleccione el cliente</label> */}
                  <ParcelClientSelector form={form} />
                </div>

                {/* Column 2: Contract Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Fecha de Inicio del contrato</label>
                  <DatePicker
                    date={form.watch('startDate')}
                    onSelect={(date: Date) => { form.setValue('startDate', date) }}
                  />
                </div>

                {/* Column 1: Sale Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tipo de Venta</label>
                  <Select
                    onValueChange={(value: SellCode) => form.setValue('sellCode', value)}
                    value={form.watch('sellCode')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SellCode.URGENCY}>Urgencia</SelectItem>
                      <SelectItem value={SellCode.PRENECESSITY}>Prenecesidad</SelectItem>
                      <SelectItem value={SellCode.TRANSFER}>Traslado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>


                <div className="space-y-2">
                  <label className="text-sm font-medium">Número de Contrato</label>
                  <Input
                    type="text"
                    placeholder="Número de contrato"
                    {...form.register('contractNumber')}
                  />
                  {form.formState.errors.contractNumber && (
                    <p className="text-sm text-red-500">{form.formState.errors.contractNumber?.message}</p>
                  )}
                  {/* <p className="text-xs text-muted-foreground">
                    Se asigna automáticamente el último número de contrato del servicio. Puede modificarlo si es necesario.
                  </p> */}
                </div>


                {/* Column 2: Seller */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Vendedor</label>
                  <Select
                    onValueChange={(value: string) => form.setValue('sellerId', value)}
                    value={form.watch('sellerId')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione un vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {sellers?.map((seller) => (
                        <SelectItem key={seller.id} value={seller.id}>
                          {getCollectorFullName(seller)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.sellerId && (
                    <p className="text-sm text-red-500">{form.formState.errors.sellerId?.message}</p>
                  )}
                </div>

                {/* Column 1: Collector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cobrador</label>
                  <Select
                    onValueChange={(value: string) => form.setValue('collectorId', value)}
                    value={form.watch('collectorId')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione un cobrador" />
                    </SelectTrigger>
                    <SelectContent>
                      {collectors?.map((collector) => (
                        <SelectItem key={collector.id} value={collector.id}>
                          {getCollectorFullName(collector)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.collectorId && (
                    <p className="text-sm text-red-500">{form.formState.errors.collectorId?.message}</p>
                  )}
                </div>

                {/* Column 1: Contract Number */}
                {/* Column 2: Service Type (read-only info) */}
                {/* <div className="space-y-2">
                  <label className="text-sm font-medium">Tipo de Servicio</label>
                  <Input
                    type="text"
                    value="Parcelas"
                    readOnly
                    className="bg-muted"
                  />
                </div> */}
              </div>

              {/* Payment method section - full width */}
              <div className="border-t pt-4 space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Método de Pago
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div
                    onClick={() => setPaymentMethod('full')}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'full' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className={`p-2 rounded-full ${paymentMethod === 'full' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <Wallet className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-bold">Pago Total</p>
                      <p className="text-xs text-muted-foreground">Abonar el 100% al contado</p>
                    </div>
                  </div>
                  <div
                    onClick={() => setPaymentMethod('plan')}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'plan' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className={`p-2 rounded-full ${paymentMethod === 'plan' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <CreditCard className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-bold">Plan de Pago</p>
                      <p className="text-xs text-muted-foreground">Financiar en cuotas</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 max-w-md">
                  <label className="text-sm font-medium">Monto Final de la Parcela</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="number"
                      step="1"
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="0.00"
                      className={`pl-10 text-lg font-bold ${paymentMethod === 'plan' ? 'bg-muted' : ''}`}
                      readOnly={paymentMethod === 'plan'}
                      {...form.register('amount', {
                        valueAsNumber: true,
                        onChange: (e) => {
                          if (paymentMethod === 'full') {
                            const value = parseFloat(e.target.value) || 0;
                            form.setValue('amount', value, { shouldValidate: true });
                          }
                        }
                      })}
                    />
                  </div>
                  {paymentMethod === 'plan' && (
                    <p className="text-xs text-muted-foreground italic">
                      Calculado automáticamente: Entrega (${(initialAmount || 0)}) + Anticipo (${(installmentAmount || 0)})
                    </p>
                  )}
                  {form.formState.errors.amount && (
                    <p className="text-sm text-red-500">{form.formState.errors.amount?.message}</p>
                  )}
                </div>

                {paymentMethod === 'full' && amount > 0 && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                    <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                      <Wallet className="h-4 w-4" />
                      <span>Se generará un cobro total por <strong>${convertToDigitStr(amount)}</strong> abonado en un solo pago.</span>
                    </p>
                  </div>
                )}

                {paymentMethod === 'plan' && (
                  <div className="space-y-4 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <input
                        type="checkbox"
                        id="has-initial-extra"
                        checked={showInitialAmount}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={(e) => {
                          setShowInitialAmount(e.target.checked)
                          if (!e.target.checked) form.setValue('initialAmount', 0)
                        }}
                      />
                      <label htmlFor="has-initial-extra" className="text-sm font-medium cursor-pointer">
                        ¿Posee un monto de entrega inicial?
                      </label>
                    </div>

                    {showInitialAmount && (
                      <div className="space-y-2 max-w-xs animate-in fade-in zoom-in duration-200">
                        <label className="text-sm font-medium">Monto de Entrega Inicial</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input
                            type="number"
                            step="1"
                            placeholder="0.00"
                            className="pl-10"
                            {...form.register('initialAmount', {
                              valueAsNumber: true,
                            })}
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Monto a financiar</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input
                            type="number"
                            step="1"
                            placeholder="0.00"
                            className="pl-10"
                            {...form.register('installmentAmount', {
                              valueAsNumber: true,
                            })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Cuotas</label>
                        <Select
                          onValueChange={(value) => form.setValue('installments', Number(value))}
                          value={form.watch('installments')?.toString()}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Cuotas" />
                          </SelectTrigger>
                          <SelectContent>
                            {installmentsOptions.map((n) => (
                              <SelectItem key={n} value={n.toString()}>
                                {n} cuotas
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {installmentsValue > 0 && (
                      <div className="p-4 bg-green-50 border border-green-100 rounded-xl animate-in fade-in slide-in-from-top-1">
                        <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                          <Wallet className="h-4 w-4" />
                          <span>
                            Se financiará el anticipo de <strong>${convertToDigitStr((installmentAmount || 0).toString())}</strong> en <strong>{(installments || 0)} cuotas</strong> de <strong>${convertToDigitStr(installmentsValue.toString())}</strong>.
                            {(form.watch('initialAmount') || 0) > 0 && ` Además hará una entrega inicial de $${convertToDigitStr((form.watch('initialAmount') || 0).toString())}.`}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => navigate({ to: Paths.Management.Parcels as any })}>
              Cancelar
            </Button>
            <Button onClick={onNext}>
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {step === 'summary' && (
        <div className="space-y-6">
          <AgreementSummary
            form={form as any}
            agreement={form.getValues()}
            client={client}
            offering={parcelOffering}
            deathData={[]}
            seller={seller}
            collector={collector}
          />

          <Card className="border-blue-100 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Detalles de la Venta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm">
                  Ubicación: Parcela <span className="font-bold">#{parcel.parcela}</span> (Sección {parcel.section}, Fila {parcel.fila})
                </p>
                <p className="text-sm">
                  Plan: <span className="font-bold">{formatSellCode(form.watch('sellCode'))}</span>
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setStep('selection')} disabled={createMutation.isPending}>
              Atrás
            </Button>
            <Button onClick={form.handleSubmit(onSubmit)} disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar Venta y Generar Pago
            </Button>
          </div>
        </div>
      )}
    </PageContainer>
  )
}